//@ts-ignore
import { tw } from "@careinfo/tailwind";
import { CrossIcon, Plus, PlusIcon } from "lucide-react";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { Control, useController } from "react-hook-form";
import GalleryIcon from "../../icons/GalleryIcon";
import Modal from "../Modal";
import CropImage from "../CropImage";

interface Props {
  name: string;
  control: Control;
  label: string;
}

export default function MultiImageField({ name, control, label }: Props) {
  const [cropModal, setCropModal] = useState(false);
  const {
    field: { value = "", onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: "",
  });

  const [imagePreview, setImagePreview] = useState<string | null>(
    value ?? null
  );
  const [CroppedImage, setCroppedImage] = useState<string | null>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (value) setImagePreview(value);
  }, [value]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileSelection(files[0]);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) handleFileSelection(files[0]);
  };

  const handleFileSelection = (file: File) => {
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        setCroppedImage(reader.result as string);
        setCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setImagePreview(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const handleCloseModal = (value) => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setCropModal(value);
  };

  return (
    <>
      <div className={tw`pb-[20px] mt-2`}>
        <p className={tw` leading-[22px] text-left text-[#030303] pb-2`}>
          {label}
        </p>
        <div
          className={tw`input_field w-full h-[200px] p-[20px] gap-[10px] border border-[#00000033] rounded-[4px] flex flex-col justify-center items-center cursor-pointer relative`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={-1}
        >
          <input
            type="file"
            ref={fileInputRef}
            className={tw`hidden`}
            accept="image/*"
            onChange={handleImageChange}
            multiple
          />
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Product"
              className={tw`max-h-full max-w-full`}
            />
          ) : (
            <>
              <GalleryIcon
                className={tw`w-[24px] h-[24px] text-[#CCCCCC] mb-[12px]`}
              />
              <div className={tw`text-center text-[#CCCCCC]`}>
                Drop Here to Upload and Preview
              </div>
            </>
          )}
          {imagePreview && (
            <button
              className={tw`text-xs bg-primary flex items-center justify-center  text-white w-8 h-8 aspect-square rounded-full  absolute top-0 right-0`}
              type="button"
              onClick={handleImageClear}
            >
              <PlusIcon className={tw`h-5 w-5 rotate-45`} />
            </button>
          )}
        </div>
        {error && (
          <div className={tw`text-red-600 text-[12px]`}>{error.message}</div>
        )}

        <Modal
          title={"Edit Image"}
          isOpen={cropModal}
          setIsOpen={handleCloseModal}
        >
          <CropImage
            imagePreview={CroppedImage}
            setImagePreview={setImagePreview}
            setCropModal={setCropModal}
            onChange={onChange}
          />
        </Modal>
      </div>
    </>
  );
}
